import { type Form as FormSchema } from '@wix/ambassador-forms-v4-form/types';
import type { ValidationError } from '@wix/form-validator';
import React from 'react';

import { useConfig } from '../../../../contexts/config-context';
import {
  type FieldActionsContextValue,
  type FieldActions,
} from '../../../../contexts/field-actions-context';
import { type ValidateFields } from '../../../../contexts/field-validation-context';
import { FormViewContext } from '../../../../contexts/form-view-context';
import { UniqueFieldSuffixContextProvider } from '../../../../contexts/unique-field-suffix-context';
import { DefaultFieldLayout } from '../../../../field-layout';
import {
  type FormView,
  type FormValues,
  type OnFieldChange,
  type OnBlur,
  type OnFocus,
  type FormFields,
} from '../../types';
import { Grid } from '../grid/grid';

import { createFormFields } from './create-form-fields';

export interface FormProps {
  formSchema: FormSchema;
  form: FormView;
  values: FormValues;
  onFieldChange: OnFieldChange;
  onBlur?: OnBlur;
  onFocus?: OnFocus;
  errors: ValidationError[];
  dataHook?: string;
  validateFields: ValidateFields;
  fields: FormFields;
  fieldActions: FieldActionsContextValue<FieldActions>;
}

const FormRoot = ({
  form,
  values,
  onFieldChange,
  onBlur,
  onFocus,
  errors,
  formSchema,
  validateFields,
  fields,
  fieldActions,
}: FormProps) => {
  const { isRTL } = useConfig();

  const formFields = createFormFields({
    form,
    values,
    onFieldChange,
    onBlur,
    onFocus,
    errors,
    FieldLayout: DefaultFieldLayout,
    formSchema,
    validateFields,
    fields,
    fieldActions,
  });

  return <Grid isRTL={isRTL} items={formFields} layout={form.grid} />;
};

export const Form = (props: FormProps) => {
  return (
    <FormViewContext.Provider value={props.form}>
      <UniqueFieldSuffixContextProvider parentId={props.form.parentId}>
        <FormRoot {...props} />
      </UniqueFieldSuffixContextProvider>
    </FormViewContext.Provider>
  );
};
